<script setup lang="ts">
import { useTranslation } from 'i18next-vue'
import Logo from '@/components/Logo.vue'
import Button from 'primevue/button'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import FooterDachmarke from '@/components/FooterDachmarke.vue'
import bmbfsfjSvg from '@/assets/images/logos/bmbfsfj.svg'
import jugendstrategieSvg from '@/assets/images/logos/jugendstrategie.svg'
import kjpImg from '@/assets/images/logos/kjp.png'
import { useAuthStore } from '@/stores/auth.store.ts'
import { getCmsPageRoute } from '@/utils/cms-pages.ts'

const versionTag = import.meta.env.VITE_APP_COMMIT_TAG
const versionRefSlug = import.meta.env.VITE_APP_COMMIT_REF_SLUG
const versionHash = import.meta.env.VITE_APP_COMMIT_SHORT_SHA

defineProps<{
    bottomOnly?: boolean
}>()

const { t } = useTranslation()

const authStore = useAuthStore()
const route = useRoute()

const participateCategory = computed(() => ({
    label: t('ui.footer.items.participate.label'),
    name: 'participate',
    children: [
        {
            label: t('ui.navigation.explore'),
            to: { name: 'explore' },
        },
        // TODO: temporarily hidden until page got expected content
        //{
        //    label: t('ui.footer.items.participate.impressions'),
        //    to: getCmsPageRoute('/eindruecke'),
        //},
        {
            label: t('ui.footer.items.participate.participationExplained'),
            to: getCmsPageRoute('/ressourcen/beteiligung-kurz-erklaert'),
        },
        {
            label: t('ui.footer.items.participate.termsExplained'),
            to: getCmsPageRoute('/ressourcen/begriffe-kurz-erklaert'),
        },
        {
            label: t('ui.footer.items.participate.faqParticipants'),
            to: getCmsPageRoute('/ressourcen/faq-fuer-teilnehmende'),
        },
        {
            label: t('ui.footer.items.participate.faqParents'),
            to: getCmsPageRoute('/ressourcen/faq-fuer-erziehungsberechtigte'),
        },
    ],
}))

const organizerCategory = computed(() => ({
    label: t('ui.footer.items.offerFormat.label'),
    name: 'participate',
    children: [
        {
            label: t('ui.footer.items.offerFormat.designParticipation'),
            to: { name: 'organizer-landing-page' },
        },
        {
            label: t('ui.footer.items.offerFormat.developFormat'),
            to: getCmsPageRoute('/ressourcen/formate-entwickeln'),
        },
        {
            label: t('ui.footer.items.offerFormat.setupApplication'),
            to: getCmsPageRoute('/ressourcen/inserat-und-anmeldung-einrichten'),
        },
        {
            label: t('ui.footer.items.offerFormat.news'),
            to: getCmsPageRoute('/ressourcen/nachrichten'),
        },
        /* TODO: enable once we have a design for this{
            label: t('ui.footer.items.offerFormat.legal'),
            to: getCmsPageRoute('/ressourcen/rechtliche-grundlagen'),
        },
         {
            label: t('ui.footer.items.offerFormat.under18'),
            to: getCmsPageRoute('/ressourcen/teilnahme-unter-18'),
        },*/
        {
            label: t('ui.footer.items.offerFormat.faq'),
            to: getCmsPageRoute('/ressourcen/faq'),
        },
    ],
}))

const items = computed(() => [
    authStore.isOrganizer || route.meta.isOrganizerSection
        ? organizerCategory.value
        : participateCategory.value,
    {
        label: t('ui.footer.items.about.label'),
        name: 'about',
        children: [
            {
                label: t('ui.footer.items.about.aboutUs'),
                to: getCmsPageRoute('/ueber-uns'),
            },
        ],
    },
    {
        label: t('ui.footer.items.contact.label'),
        name: 'contact',
        children: [
            {
                label: t('ui.footer.items.contact.writeUs'),
                to: { name: 'contact' },
            },
            /*  TODO: enable once we have a design for this {
                label: t('ui.footer.items.contact.newsletter'),
                to: { name: 'explore' },
            }, */
            {
                label: t('ui.footer.items.contact.instagram'),
                href: 'https://www.instagram.com/jugend.strategie/',
                newTab: true,
            },
        ],
    },
])

const bottomLinks = computed(() => [
    {
        label: t('ui.footer.bottomLinks.imprint'),
        to: getCmsPageRoute('/impressum'),
    },
    // TODO: This menu item is currently disabled until we finalized the terms
    //{
    //    label: t('ui.footer.bottomLinks.tos'),
    //    to: getCmsPageRoute('/nutzungsbedingungen'),
    //},
    {
        label: t('ui.footer.bottomLinks.privacy'),
        to: getCmsPageRoute('/datenschutz'),
    },
    {
        label: t('ui.footer.bottomLinks.accessibility'),
        to: getCmsPageRoute('/barrierefreiheit'),
    },
    {
        label: t('ui.footer.bottomLinks.easyRead'),
        to: getCmsPageRoute('/leichte-sprache'),
    },
    /*  TODO: enable once we have a design for this {
        label: t('ui.footer.bottomLinks.cookies'),
        to: getCmsPageRoute('/datenschutz'),
    },*/
])

const logosItems = computed(() => [
    {
        label: t('ui.footer.sponsors.bmbfsfj.label'),
        name: t('ui.footer.sponsors.bmbfsfj.name'),
        image: bmbfsfjSvg,
    },
    {
        label: t('ui.footer.sponsors.youthStrategy.label'),
        name: t('ui.footer.sponsors.youthStrategy.name'),
        image: jugendstrategieSvg,
    },
    {
        label: t('ui.footer.sponsors.fund.label'),
        name: t('ui.footer.sponsors.fund.name'),
        image: kjpImg,
    },
])

const mobileCollapsibleItems = ref<string[]>([])

function toggleMobileCollapsibleItem(name: string) {
    const index = mobileCollapsibleItems.value.indexOf(name)
    if (index === -1) {
        mobileCollapsibleItems.value.push(name)
        return
    }

    mobileCollapsibleItems.value.splice(index, 1)
}
</script>
<template>
    <footer class="flex flex-col items-center bg-white">
        <template v-if="!bottomOnly">
            <div
                class="grid grid-cols-1 md:grid-cols-4 gap-10 w-full max-w-7xl pt-10 pb-10 px-4 md:p-10 md:pb-6"
            >
                <div class="pl-3">
                    <Logo />
                </div>
                <div v-for="(item, itemsI) in items" :key="itemsI" class="space-y-2">
                    <div class="pl-3 flex justify-between items-center">
                        <span :id="`footer-section-${item.name}-label`" class="heading-base block">
                            {{ item.label }}</span
                        >
                        <Button
                            :icon="`pi pi-chevron-${mobileCollapsibleItems.includes(item.name) ? 'up' : 'down'}`"
                            class="md:hidden"
                            size="small"
                            text
                            severity="contrast"
                            :data-testid="`footer-item-mobile-collapsible-${itemsI}`"
                            :aria-labelledby="`footer-section-${item.name}-label`"
                            :aria-expanded="mobileCollapsibleItems.includes(item.name)"
                            :aria-controls="`footer-section-${item.name}-panel`"
                            @click="toggleMobileCollapsibleItem(item.name)"
                        />
                    </div>

                    <div
                        :id="`footer-section-${item.name}-panel`"
                        :class="mobileCollapsibleItems.includes(item.name) ? 'block' : 'hidden'"
                        class="md:block"
                    >
                        <Button
                            v-for="(child, childI) in item.children"
                            :key="childI"
                            text
                            :label="child.label"
                            :as="'href' in child ? 'a' : 'router-link'"
                            v-bind="'href' in child ? { href: child.href } : { to: child.to }"
                            fluid
                            severity="contrast"
                            class="justify-start"
                            size="small"
                            :data-testid="`footer-item-${itemsI}-${childI}`"
                            :target="'newTab' in child && child.newTab ? '_blank' : undefined"
                            :rel="
                                'newTab' in child && child.newTab
                                    ? 'noopener noreferrer'
                                    : undefined
                            "
                        />
                    </div>
                </div>
            </div>

            <div
                class="grid grid-cols-2 md:grid-cols-4 gap-10 w-full max-w-7xl pt-10 pb-10 px-4 md:p-10 md:pb-6"
            >
                <FooterDachmarke />

                <div v-for="(item, itemsI) in logosItems" :key="itemsI" class="space-y-2">
                    <span class="heading-base block px-3 py-2">{{ item.label }}</span>

                    <div class="px-3 space-y-4">
                        <img
                            :src="item.image"
                            class="h-14"
                            :alt="item.name"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
            </div>
        </template>
        <div class="bg-surface-100 w-full flex justify-center">
            <div
                class="flex flex-wrap justify-between gap-6 w-full max-w-7xl items-center pt-10 pb-10 px-4 md:p-10 md:pt-6"
            >
                <div class="flex flex-wrap gap-6 items-center">
                    <Button
                        v-for="(link, i) of bottomLinks"
                        :key="i"
                        :label="link.label"
                        :as="'href' in link ? 'a' : 'router-link'"
                        v-bind="'href' in link ? { href: link.href } : { to: link.to }"
                        size="small"
                        text
                        severity="contrast"
                        :data-testid="`footer-bottom-link-${i}`"
                    />
                </div>

                <p class="px-3">
                    {{
                        t('ui.footer.bottomLinks.copyright', {
                            year: new Date().getFullYear(),
                        })
                    }}
                </p>

                <p class="text-xs text-neutral-600 px-3">
                    <span>{{ t('ui.appName') }}</span>

                    <span v-if="versionTag || versionRefSlug">
                        -
                        <span class="bg-surface-200 py-0.5 px-1 rounded-sm">
                            {{ versionTag || versionRefSlug }}
                        </span>
                    </span>

                    <span v-if="versionHash"> #{{ versionHash }} </span>
                </p>
            </div>
        </div>
    </footer>
</template>
